import * as z from 'zod/v4';

import { type DrawNumber, drawNumberSchema } from '../draw/entry.js';
import { type CouponProduct, productEnum } from '../product/entry.js';

/**
 * Coupon types
 */
const COUPON_TYPE = ['standard', 'singlerows', 'external_system'] as const;

/**
 * Schema for coupon types
 */
export const couponTypeSchema = z.enum(COUPON_TYPE);

/**
 * Type for coupon types
 */
export type CouponType = z.infer<typeof couponTypeSchema>;

/**
 * Tipsen Coupon ID
 */
export type CouponId = `${CouponProduct}_${DrawNumber}_${CouponType}`;

/**
 * Zod schema for CouponId
 */
export const couponIdSchema = z.custom<CouponId>((value) => {
  if (typeof value !== 'string') {
    return false;
  }

  const [product, drawNumber, couponType, ...rest] = value.split('_');

  if (rest.length > 0) {
    return false;
  }

  try {
    productEnum.parse(product);
    const coercedDrawNumber = z.coerce.number().parse(drawNumber);
    drawNumberSchema.parse(coercedDrawNumber);
    couponTypeSchema.parse(couponType);
  } catch {
    return false;
  }

  return true;
});

/**
 * Utility function to get a coupon id.
 *
 * @param product Product identifier
 * @param drawNumber Draw number identifier
 * @param type Coupon type identifier
 * @returns Correct coupon id
 */
export function getCouponId(product: CouponProduct, drawNumber: DrawNumber, type: CouponType): CouponId {
  return `${product}_${drawNumber}_${type}`;
}
